import { SERVICE_TYPE_LABEL } from "settings/account/components/Subscriptions/SubscriptionsData";
import {
	endFromDuration,
	formatMexicoCityWindow,
	MEXICO_CITY_TIME_ZONE,
	utcIsoToZonedInput,
	zonedInputToUtcIso,
} from "./maintenanceTime";
import type {
	MaintenanceRecordRow,
	MaintenanceRecordStatusFilter,
	MaintenanceRecordUpdatePayload,
	MaintenanceService,
	MaintenanceServiceType,
	MaintenanceWindowPayload,
	TenantOption,
	TenantServiceOption,
} from "./types";

/**
 * Pure rules behind Admin Center → Maintenance windows (CTX-1294): what the
 * forms accept, how they become requests, how the picker groups services, and
 * how the list folds one-row-per-customer back into windows. The components
 * only wire these to inputs.
 */

/** The registry's labels, plus the two kinds a subscription row never names. */
export const MAINTENANCE_SERVICE_TYPE_LABEL: Record<MaintenanceServiceType, string> = {
	...SERVICE_TYPE_LABEL,
	extender_pop: "Extended PoP CPE",
	carrier_access: "CPE Backhaul",
};

export function serviceTypeLabel(serviceType: string): string {
	return MAINTENANCE_SERVICE_TYPE_LABEL[serviceType as MaintenanceServiceType] ?? serviceType;
}

/** The list's tabs. */
export const STATUS_FILTER_TABS: Array<{ value: MaintenanceRecordStatusFilter; label: string }> = [
	{ value: "upcoming", label: "Upcoming" },
	{ value: "past", label: "Past" },
	{ value: "all", label: "All" },
];

// ---------------------------------------------------------------------------
// Service picker
// ---------------------------------------------------------------------------

export function serviceKey(service: Pick<MaintenanceService, "serviceType" | "serviceId">): string {
	return `${service.serviceType}:${service.serviceId}`;
}

/**
 * What a service reads as in a list: its id when it has one, its name
 * otherwise, and its kind and number when it has neither — legacy services
 * can lack both, and the picker sorts and renders this value.
 */
export function serviceDisplayCode(service: MaintenanceService): string {
	return (
		service.serviceCode ||
		service.serviceName ||
		`${serviceTypeLabel(service.serviceType)} #${service.serviceId}`
	);
}

/** Case-insensitive match on the service id, name or type. Blank matches all. */
export function filterServiceOptions(
	options: TenantServiceOption[],
	term: string
): TenantServiceOption[] {
	const needle = term.trim().toLowerCase();
	if (!needle) return options;
	return options.filter((option) =>
		[option.serviceCode ?? "", option.serviceName ?? "", serviceTypeLabel(option.serviceType)].some(
			(value) => value.toLowerCase().includes(needle)
		)
	);
}

/** Customers matching a search on name or customer id. Blank matches all. */
export function filterTenantOptions(options: TenantOption[], term: string): TenantOption[] {
	const needle = term.trim().toLowerCase();
	if (!needle) return options;
	return options.filter(
		(option) =>
			option.name.toLowerCase().includes(needle) ||
			(option.chargebeeCustomerId ?? "").toLowerCase().includes(needle)
	);
}

export interface ServiceGroup {
	tenant: TenantOption;
	services: TenantServiceOption[];
}

/**
 * The picker's sections: one per selected customer, in the order they were
 * selected, services sorted by id. A customer with no services still gets a
 * section — "this customer has nothing to pick" is an answer the operator
 * needs, and it is still affected as a whole.
 */
export function groupServicesByTenant(
	options: TenantServiceOption[],
	tenants: TenantOption[]
): ServiceGroup[] {
	return tenants.map((tenant) => ({
		tenant,
		services: options
			.filter((option) => option.tenantId === tenant.id)
			.sort((a, b) => serviceDisplayCode(a).localeCompare(serviceDisplayCode(b))),
	}));
}

/**
 * Drop the selected services whose customer is no longer selected. Without it
 * a deselected customer would still be sent through its services, and the
 * backend refuses a service outside `tenantIds`.
 */
export function pruneServicesToTenants(
	selected: TenantServiceOption[],
	tenantIds: number[]
): TenantServiceOption[] {
	const kept = new Set(tenantIds);
	return selected.filter((service) => kept.has(service.tenantId));
}

// ---------------------------------------------------------------------------
// Create form
// ---------------------------------------------------------------------------

export interface MaintenanceWindowForm {
	title: string;
	message: string;
	/** `datetime-local` values, read in Mexico City time. */
	startsAtLocal: string;
	endsAtLocal: string;
	/** Kept as typed: "" means "not estimated". */
	expectedImpactMinutes: string;
	tenants: TenantOption[];
	services: TenantServiceOption[];
}

export const EMPTY_WINDOW_FORM: MaintenanceWindowForm = {
	title: "",
	message: "",
	startsAtLocal: "",
	endsAtLocal: "",
	expectedImpactMinutes: "",
	tenants: [],
	services: [],
};

const MAX_TITLE_LENGTH = 255;

/** The backend takes at most this many customers per window. */
export const MAX_CUSTOMERS_PER_WINDOW = 100;

/** Start and end as UTC ISO, or the reasons they cannot be. */
function validateWindowTimes(
	startsAtLocal: string,
	endsAtLocal: string
): { startsAt: string | null; endsAt: string | null; errors: string[] } {
	const startsAt = zonedInputToUtcIso(startsAtLocal, MEXICO_CITY_TIME_ZONE);
	const endsAt = zonedInputToUtcIso(endsAtLocal, MEXICO_CITY_TIME_ZONE);
	const errors: string[] = [];
	if (!startsAt) errors.push("Set when the window starts.");
	if (!endsAt) errors.push("Set when the window ends.");
	if (startsAt && endsAt && new Date(endsAt) <= new Date(startsAt)) {
		errors.push("The window must end after it starts.");
	}
	return { startsAt, endsAt, errors };
}

/**
 * Everything wrong with the form, in reading order; empty when it can be sent.
 * Mirrors the backend's rules so the operator learns about them before the
 * round trip — the backend checks them again regardless.
 */
export function validateWindowForm(form: MaintenanceWindowForm): string[] {
	const errors: string[] = [];
	if (!form.title.trim()) errors.push("Add a title.");
	if (form.title.trim().length > MAX_TITLE_LENGTH) {
		errors.push(`Keep the title under ${MAX_TITLE_LENGTH} characters.`);
	}
	if (!form.message.trim()) errors.push("Add the message customers will read.");
	errors.push(...validateWindowTimes(form.startsAtLocal, form.endsAtLocal).errors);

	const impact = form.expectedImpactMinutes.trim();
	if (impact && !/^\d+$/.test(impact)) {
		errors.push("Expected impact is a whole number of minutes.");
	}

	if (form.tenants.length === 0) errors.push("Select at least one customer.");
	if (form.tenants.length > MAX_CUSTOMERS_PER_WINDOW) {
		errors.push(`Select at most ${MAX_CUSTOMERS_PER_WINDOW} customers.`);
	}
	return errors;
}

/** The request body. Call only once `validateWindowForm` returns no errors. */
export function buildWindowPayload(form: MaintenanceWindowForm): MaintenanceWindowPayload {
	const impact = form.expectedImpactMinutes.trim();
	const tenantIds = form.tenants.map((tenant) => tenant.id);
	return {
		title: form.title.trim(),
		message: form.message.trim(),
		startsAt: zonedInputToUtcIso(form.startsAtLocal, MEXICO_CITY_TIME_ZONE) ?? "",
		endsAt: zonedInputToUtcIso(form.endsAtLocal, MEXICO_CITY_TIME_ZONE) ?? "",
		expectedImpactMinutes: impact ? Number(impact) : null,
		tenantIds,
		services: pruneServicesToTenants(form.services, tenantIds).map(
			({ serviceType, serviceId }) => ({ serviceType, serviceId })
		),
	};
}

// ---------------------------------------------------------------------------
// Description preview
// ---------------------------------------------------------------------------

const WINDOW_LINE_PREFIX = "Window: ";

/** Fixed last line: questions go through a support ticket, not a person. */
const SUPPORT_LINE = "Questions or issues after the window? Open a support ticket.";

function windowLine(startsAt: string, endsAt: string): string | null {
	const range = formatMexicoCityWindow(startsAt, endsAt);
	return range ? `${WINDOW_LINE_PREFIX}${range} (Mexico City time)` : null;
}

function formatServiceForCustomer(service: TenantServiceOption): string {
	const code = service.serviceCode || `${service.serviceType} #${service.serviceId}`;
	return service.serviceName ? `${code} (${service.serviceName})` : code;
}

/**
 * The text `tenant` will read, line for line what the backend composes
 * (`controllers/KontrolEdge/service-maintenance/description.js`). Only a
 * preview: the backend writes the stored text, so a drift between the two is
 * a wrong preview, never a wrong notice. Null until the hours are valid.
 */
export function composeDescriptionPreview(
	form: MaintenanceWindowForm,
	tenant: TenantOption
): string | null {
	const { startsAt, endsAt } = validateWindowTimes(form.startsAtLocal, form.endsAtLocal);
	if (!startsAt || !endsAt || new Date(endsAt) <= new Date(startsAt)) return null;
	const hours = windowLine(startsAt, endsAt);
	if (!hours) return null;

	const lines = [form.title.trim(), form.message.trim(), hours];
	const impact = form.expectedImpactMinutes.trim();
	if (/^\d+$/.test(impact)) lines.push(`Expected impact: up to ${Number(impact)} minutes`);

	const services = form.services.filter((service) => service.tenantId === tenant.id);
	lines.push(
		services.length > 0
			? `Affected services: ${services.map(formatServiceForCustomer).join(", ")}`
			: "Affected services: all services"
	);

	lines.push(SUPPORT_LINE);
	return lines.join("\n");
}

// ---------------------------------------------------------------------------
// Edit form (one row)
// ---------------------------------------------------------------------------

export interface MaintenanceRecordEditForm {
	startsAtLocal: string;
	endsAtLocal: string;
	description: string;
}

/** A stored row back into the edit form. The end comes from start + duration. */
export function editFormFromRecord(record: MaintenanceRecordRow): MaintenanceRecordEditForm {
	const endsAt = endFromDuration(record.date, record.duration);
	return {
		startsAtLocal: utcIsoToZonedInput(record.date, MEXICO_CITY_TIME_ZONE),
		endsAtLocal: endsAt ? utcIsoToZonedInput(endsAt, MEXICO_CITY_TIME_ZONE) : "",
		description: record.description,
	};
}

export function validateEditForm(form: MaintenanceRecordEditForm): string[] {
	const errors = validateWindowTimes(form.startsAtLocal, form.endsAtLocal).errors;
	if (!form.description.trim()) errors.push("The description cannot be empty.");
	return errors;
}

export function buildUpdatePayload(form: MaintenanceRecordEditForm): MaintenanceRecordUpdatePayload {
	return {
		startsAt: zonedInputToUtcIso(form.startsAtLocal, MEXICO_CITY_TIME_ZONE) ?? "",
		endsAt: zonedInputToUtcIso(form.endsAtLocal, MEXICO_CITY_TIME_ZONE) ?? "",
		description: form.description,
	};
}

/**
 * Rewrite the "Window: …" line to new hours, leaving every other line as the
 * operator wrote it. The description is saved verbatim, so without this a
 * moved window would keep telling the customer the old hours. A description
 * with no such line is returned untouched, as are hours that do not parse.
 */
export function replaceWindowLine(
	description: string,
	startsAtLocal: string,
	endsAtLocal: string
): string {
	const { startsAt, endsAt, errors } = validateWindowTimes(startsAtLocal, endsAtLocal);
	if (errors.length > 0 || !startsAt || !endsAt) return description;
	const line = windowLine(startsAt, endsAt);
	if (!line) return description;
	return description
		.split("\n")
		.map((existing) => (existing.startsWith(WINDOW_LINE_PREFIX) ? line : existing))
		.join("\n");
}

// ---------------------------------------------------------------------------
// List
// ---------------------------------------------------------------------------

export interface RecordGroup {
	key: string;
	/** The first line of the description: the window's title when the backend wrote it. */
	heading: string;
	date: string;
	duration: number;
	records: MaintenanceRecordRow[];
}

export function firstLine(text: string): string {
	return text.split("\n", 1)[0].trim();
}

/**
 * Fold rows that look like one window — same start, same duration, same
 * first line — under one heading, in the order the API returned them.
 *
 * Purely visual: no column links the rows of a window, so two windows that
 * coincide on all three would share a heading, and a row edited on its own
 * leaves its group. Every action still applies to one row.
 */
export function groupRecordsByWindow(records: MaintenanceRecordRow[]): RecordGroup[] {
	const groups = new Map<string, RecordGroup>();
	for (const record of records) {
		const heading = firstLine(record.description);
		const key = `${record.date}|${record.duration}|${heading}`;
		const group = groups.get(key);
		if (group) {
			group.records.push(record);
		} else {
			groups.set(key, { key, heading, date: record.date, duration: record.duration, records: [record] });
		}
	}
	return Array.from(groups.values());
}
